import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Founder UX sprint — "The platform must explain itself".
 * One dismissible purpose strip per workspace, mounted once inside Topbar.
 * Each entry answers: what is this screen, why come here, what happens next
 * and where the workflow continues. Content only — no logic changes.
 * `g` marks the shared-governance trio (Studio ↔ Library ↔ Approval Center).
 */
const GOV = {
  es: 'Studio, Biblioteca y Centro de Aprobaciones no son módulos independientes: son tres vistas del mismo proceso de gobernanza. Creas y versionas en el Studio → envías a revisión → apruebas y publicas en el Centro de Aprobaciones → el activo publicado vive en la Biblioteca, listo para asignarse.',
  en: 'Studio, Library and Approval Center are not independent modules: they are three views of the same governance process. You author and version in the Studio → submit for review → approve & publish in the Approval Center → the published asset lives in the Library, ready to assign.',
};

const R = {
  '/admin': {
    es: 'Tu centro de mando: indicadores del negocio y alertas operativas. Desde aquí saltas a lo que requiere acción.',
    en: 'Your command center: business indicators and operational alerts. Jump from here to whatever needs action.',
  },
  '/admin/intelligence': {
    es: 'Capa de analítica e IA sobre los datos de todas las suites. Solo lectura: aquí se interpreta, no se opera.',
    en: 'AI analytics layer across all suites. Read-only: interpret here, operate elsewhere.',
  },
  '/admin/competencies': {
    es: 'Catálogo maestro de competencias de TalentCompetency. Lo que definas aquí alimenta perfiles de puesto y evaluaciones de competencias. Las dimensiones de clima de CLIMARA tienen su propio catálogo.',
    en: 'Master competency catalog for TalentCompetency. What you define here feeds position profiles and competency evaluations. CLIMARA climate dimensions live in their own catalog.',
  },
  '/admin/role-profiles': {
    es: 'Perfiles de puesto: el patrón contra el que se comparan las evaluaciones. Se crean aquí y se usan al asignar evaluaciones.',
    en: 'Position profiles: the benchmark evaluations are compared against. Created here, used when assigning evaluations.',
  },
  '/admin/questionnaires': {
    es: 'La Biblioteca es donde viven los activos de evaluación y su estado de gobernanza. Para CREAR o EDITAR contenido usa el Assessment Studio; desde aquí consultas, publicas versiones aprobadas y asignas.',
    en: 'The Library is where assessment assets and their governance state live. To CREATE or EDIT content use the Assessment Studio; from here you browse, publish approved versions and assign.',
    g: true,
  },
  '/admin/resource-center': {
    es: 'Repositorio de recursos y materiales de apoyo para consultores y clientes.',
    en: 'Repository of resources and support materials for consultants and clients.',
  },
  '/admin/studio': {
    es: 'Tu experiencia principal de autoría: aquí CREAS, EDITAS, VERSIONAS y PUBLICAS evaluaciones. Al enviar a revisión, la aprobación ocurre en el Centro de Aprobaciones y el activo publicado aparece en la Biblioteca.',
    en: 'Your primary authoring experience: you CREATE, EDIT, VERSION and PUBLISH assessments here. When you submit for review, approval happens in the Approval Center and the published asset appears in the Library.',
    g: true,
  },
  '/admin/clients': {
    es: 'Cartera de empresas cliente: aquí gestionas cuentas, suites activadas y accesos de administradores del cliente.',
    en: 'Client company portfolio: manage accounts, activated suites and client admin access here.',
  },
  '/admin/users': {
    es: 'Todas las personas de la plataforma (colaboradores evaluados, administradores de cliente y equipo CTH). Las evaluaciones se asignan en Evaluaciones, no aquí.',
    en: 'Everyone on the platform (evaluated collaborators, client admins and the CTH team). Evaluations are assigned in Evaluations, not here.',
  },
  '/admin/advisors': {
    es: 'Equipo de asesores CTH: asignación de cartera y supervisión de carga de trabajo.',
    en: 'CTH advisor team: portfolio assignment and workload supervision.',
  },
  '/admin/crm': {
    es: 'Pipeline comercial: prospectos y oportunidades antes de convertirse en clientes.',
    en: 'Commercial pipeline: prospects and opportunities before they become clients.',
  },
  '/admin/commercial-dashboard': {
    es: 'Indicadores comerciales agregados para dirección.',
    en: 'Aggregated commercial indicators for leadership.',
  },
  '/admin/recruitment': {
    es: 'Procesos de reclutamiento y candidatos por vacante.',
    en: 'Recruitment processes and candidates per vacancy.',
  },
  '/admin/reports': {
    es: 'Sala de revisión de informes: aquí el asesor revisa, edita y PUBLICA los informes al cliente. Un informe no es visible para el cliente hasta que lo publicas; aparecerá en su sección Informes.',
    en: 'Report review room: the advisor reviews, edits and RELEASES reports to the client here. A report is not visible to the client until you release it; it will appear in their Reports section.',
  },
  '/admin/leads': {
    es: 'Tu bandeja operativa: TODA solicitud pendiente (clientes, suites, evaluaciones, campañas Climara, revisiones de biblioteca) se decide aquí. Al aprobar, el sistema crea el siguiente paso automáticamente y la contraparte queda notificada.',
    en: 'Your operational inbox: EVERY pending request (clients, suites, evaluations, Climara campaigns, library reviews) is decided here. On approval the system creates the next step automatically and the counterpart is notified.',
    g: true,
  },
  '/admin/suites': {
    es: 'Catálogo de suites del producto y su activación por cliente.',
    en: 'Product suite catalog and per-client activation.',
  },
  '/admin/talentcheck': {
    es: 'Espacio TalentCheck: verificación y evaluación individual. Flujo: solicitud del cliente → aprobación → evaluación en Evaluaciones → informe revisado y publicado en Informes.',
    en: 'TalentCheck workspace: individual verification and assessment. Flow: client request → approval → evaluation in Evaluations → report reviewed and released in Reports.',
  },
  '/admin/climara': {
    es: 'Espacio CLIMARA: campañas de clima anónimas por oleadas. La consola de campaña te muestra fase, participación y próximo paso; el reporte ejecutivo se genera manualmente cuando decidas que la participación es suficiente.',
    en: 'CLIMARA workspace: anonymous climate campaigns in waves. The campaign management view shows phase, participation and next step; the executive report is generated manually when you decide participation is sufficient.',
  },
  '/admin/talentmap': {
    es: 'Espacio TalentMap: mapeo conductual y cognitivo individual. Mismo flujo compartido: solicitud → aprobación → evaluación → informe publicado.',
    en: 'TalentMap workspace: individual behavioral and cognitive mapping. Same shared flow: request → approval → evaluation → released report.',
  },
  '/admin/talenttrain': {
    es: 'Espacio TalentTrain: planes de formación. Normalmente se crean desde una evaluación completada («Crear plan de formación») o por activación de programa del cliente.',
    en: 'TalentTrain workspace: training plans. Usually created from a completed evaluation ("Create training plan") or via a client program activation.',
  },
  '/admin/talentcompetency': {
    es: 'Espacio TalentCompetency: perfiles de competencias y sus evaluaciones. Un perfil solo puede publicarse con al menos una evaluación publicada vinculada; después el cliente puede solicitar evaluaciones contra ese perfil.',
    en: 'TalentCompetency workspace: competency profiles and their evaluations. A profile can only be published with at least one linked published assessment; afterwards the client can request evaluations against it.',
  },
  '/admin/talentstart': {
    es: 'Espacio TalentStart: onboarding estructurado de nuevos colaboradores tras la activación del programa.',
    en: 'TalentStart workspace: structured onboarding of new collaborators after program activation.',
  },
  '/admin/talentheart': {
    es: 'Espacio TalentHeart: compromiso continuo (reconocimientos, cumpleaños, intereses). No tiene estado final: es un programa permanente con reportes periódicos.',
    en: 'TalentHeart workspace: continuous engagement (recognitions, birthdays, interests). It has no terminal state: it is an always-on program with periodic reports.',
  },
  '/admin/talentfix': {
    es: 'Espacio TalentFix: diagnóstico organizacional por fases (consentimiento y contexto del cliente → diagnóstico → planes de acción → informes).',
    en: 'TalentFix workspace: phased organizational diagnosis (client consent & context → diagnosis → action plans → reports).',
  },
  '/admin/talentintelligence': {
    es: 'Vista ejecutiva de TalentIntelligence sobre los resultados de todas las suites.',
    en: 'TalentIntelligence executive view across all suite results.',
  },
  '/admin/audit': {
    es: 'Registro de auditoría del sistema: quién hizo qué y cuándo.',
    en: 'System audit log: who did what and when.',
  },
  '/admin/trash': {
    es: 'Papelera: elementos eliminados recuperables por el administrador.',
    en: 'Trash: deleted items recoverable by the administrator.',
  },
  // ---------------- Client Admin ----------------
  '/client': {
    es: 'Tu panel general: estado de tus suites, solicitudes y últimos informes publicados por el equipo CTH.',
    en: 'Your overview: suite status, requests and the latest reports released by the CTH team.',
  },
  '/client/requests': {
    es: 'Aquí lanzas evaluaciones en autogestión (sin aprobación interna) y, cuando lo necesites, solicitas apoyo del equipo CTH. Solo las solicitudes de apoyo pasan a revisión del equipo; su estado y conversación se actualizan aquí.',
    en: 'Launch self-service evaluations here (no internal approval needed) and, whenever you need it, request CTH team support. Only support requests go to the team for review; their status and conversation update here.',
  },
  '/client/reports': {
    es: 'Los informes aparecen aquí cuando el equipo CTH los revisa y publica. Si esperas un informe, revisa el estado de tu solicitud o el avance en la suite correspondiente.',
    en: 'Reports appear here once the CTH team reviews and releases them. If you are waiting for a report, check your request status or the progress in the corresponding suite.',
  },
  '/client/employees': {
    es: 'Tu directorio de colaboradores: las personas que participan en evaluaciones y programas.',
    en: 'Your collaborator directory: the people who participate in evaluations and programs.',
  },
  '/client/climara': {
    es: 'Panel CLIMARA: sigue la campaña de clima en curso (fase, participación y próximo paso). Las respuestas son 100% anónimas; el reporte ejecutivo aparecerá aquí cuando CTH lo publique.',
    en: 'CLIMARA panel: follow the ongoing climate campaign (phase, participation and next step). Responses are 100% anonymous; the executive report will appear here once CTH releases it.',
  },
};

export const WorkspacePurposeBanner = () => {
  const { pathname } = useLocation();
  const { i18n, t } = useTranslation();
  const entry = R[pathname.replace(/\/$/, '')] || null;
  const storageKey = `purpose_dismissed:${pathname}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(storageKey) === '1'; } catch { return false; }
  });
  if (!entry || dismissed) return null;
  const isES = (i18n.language || 'es').toLowerCase().startsWith('es');
  const dismiss = () => {
    try { localStorage.setItem(storageKey, '1'); } catch { /* noop */ }
    setDismissed(true);
  };
  return (
    <div className="px-6 lg:px-8 pb-3 -mt-1" data-testid="workspace-purpose-banner">
      <div className="flex items-start gap-2.5 rounded-md border border-primary/20 bg-primary/[0.04] px-3 py-2">
        <Info className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
        <div className="flex-1 text-xs text-muted-foreground leading-relaxed">
          <p>{isES ? entry.es : entry.en}</p>
          {entry.g && <p className="mt-1 text-xs italic">{isES ? GOV.es : GOV.en}</p>}
        </div>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0 text-muted-foreground"
                onClick={dismiss} aria-label={t('common.dismiss', { defaultValue: 'Dismiss' })}
                data-testid="workspace-purpose-dismiss">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

export default WorkspacePurposeBanner;
